/**
 * Origem do lead em anúncio Click-to-WhatsApp (Meta): campanha, conjunto,
 * anúncio, UTMs e IDs de rastreamento. Não renderiza nada quando o contato não
 * veio de anúncio.
 */
import React, { useEffect, useState } from 'react';
import { Box, Chip, Collapse, Divider, IconButton, Tooltip, Typography } from '@mui/material';
import {
  Campaign as CampaignIcon,
  ContentCopy as CopyIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import axios from '../config/axios';
import { getApiUrl } from '../config/environment';

interface AdAttribution {
  id: string;
  touch_number: number;
  received_at: string;
  source_type: string | null;
  source_id: string | null;
  source_url: string | null;
  headline: string | null;
  body: string | null;
  image_url: string | null;
  ctwa_clid: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  utm_id: string | null;
  fbclid: string | null;
  url_params: Record<string, string> | null;
  ad_name: string | null;
  adset_id: string | null;
  adset_name: string | null;
  campaign_id: string | null;
  campaign_name: string | null;
  campaign_objective: string | null;
  ad_account_id: string | null;
  enrichment_status: string;
  enrichment_error: string | null;
  waba_id: string | null;
}

interface Props {
  contactId?: string | null;
  patientId?: string | null;
  /** Quando false, adia a requisição (prioriza carregar cadastro/agenda do paciente). */
  fetchEnabled?: boolean;
}

const formatDateTime = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

const networkLabel = (t: AdAttribution) => {
  const url = (t.source_url || '').toLowerCase();
  if (url.includes('instagram')) return 'Instagram';
  if (url.includes('facebook') || url.includes('fb.')) return 'Facebook';
  return 'Meta';
};

const Field: React.FC<{ label: string; value?: string | null; copy?: boolean }> = ({ label, value, copy }) => {
  if (!value) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, py: 0.25 }}>
      <Typography variant="caption" sx={{ color: '#64748b', minWidth: 92, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ color: '#0f172a', fontWeight: 600, wordBreak: 'break-all', flex: 1 }}>
        {value}
      </Typography>
      {copy && (
        <Tooltip title="Copiar">
          <IconButton size="small" sx={{ p: 0.25 }} onClick={() => void navigator.clipboard?.writeText(value)}>
            <CopyIcon sx={{ fontSize: 13, color: '#94a3b8' }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

const TouchDetails: React.FC<{ touch: AdAttribution }> = ({ touch }) => {
  const knownKeys = new Set(['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id', 'fbclid']);
  const extraParams = Object.entries(touch.url_params || {}).filter(([k]) => !knownKeys.has(k));
  return (
    <Box>
      <Field label="Campanha" value={touch.campaign_name || touch.campaign_id} />
      <Field label="Conjunto" value={touch.adset_name || touch.adset_id} />
      <Field label="Anúncio" value={touch.ad_name || touch.source_id} />
      <Field label="Objetivo" value={touch.campaign_objective} />
      <Field label="Título" value={touch.headline} />
      <Field label="utm_source" value={touch.utm_source} />
      <Field label="utm_medium" value={touch.utm_medium} />
      <Field label="utm_campaign" value={touch.utm_campaign} />
      <Field label="utm_content" value={touch.utm_content} />
      <Field label="utm_term" value={touch.utm_term} />
      <Field label="utm_id" value={touch.utm_id} />
      <Field label="fbclid" value={touch.fbclid} copy />
      {extraParams.map(([k, v]) => (
        <Field key={k} label={k} value={v} />
      ))}
      <Field label="ID campanha" value={touch.campaign_id} copy />
      <Field label="ID conjunto" value={touch.adset_id} copy />
      <Field label="ID anúncio" value={touch.source_id} copy />
      <Field label="Conta de anúncio" value={touch.ad_account_id} copy />
      <Field label="ctwa_clid" value={touch.ctwa_clid} copy />
      <Field label="WABA" value={touch.waba_id} copy />
      <Field label="Link" value={touch.source_url} copy />
      {touch.enrichment_status === 'failed' && (
        <Typography variant="caption" sx={{ color: '#b45309', display: 'block', mt: 0.5 }}>
          Nome da campanha indisponível: conecte uma conta de anúncios com permissão ads_read.
        </Typography>
      )}
    </Box>
  );
};

const LeadAdOriginCard: React.FC<Props> = ({ contactId, patientId, fetchEnabled = true }) => {
  const [touches, setTouches] = useState<AdAttribution[]>([]);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const path = contactId
      ? `/contacts/${contactId}/ad-attributions`
      : patientId
        ? `/clinic/patients/${patientId}/ad-attributions`
        : null;
    setTouches([]);
    if (!path || !fetchEnabled) return;
    axios
      .get(getApiUrl(path))
      .then((res) => {
        if (!cancelled) setTouches(Array.isArray(res.data?.data) ? res.data.data : []);
      })
      .catch(() => {
        if (!cancelled) setTouches([]);
      });
    return () => {
      cancelled = true;
    };
  }, [contactId, patientId, fetchEnabled]);

  if (touches.length === 0) return null;

  const latest = touches[0];
  const first = touches[touches.length - 1];
  const title = first.campaign_name || first.headline || first.ad_name || 'Anúncio Meta';

  return (
    <Box
      sx={{
        mb: 1.5,
        p: 1.25,
        borderRadius: 2,
        border: '1px solid #dbeafe',
        bgcolor: '#f8fbff',
      }}
    >
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
        onClick={() => setExpanded((v) => !v)}
      >
        {first.image_url ? (
          <Box
            component="img"
            src={first.image_url}
            alt=""
            sx={{ width: 36, height: 36, borderRadius: 1, objectFit: 'cover', flexShrink: 0 }}
          />
        ) : (
          <CampaignIcon sx={{ color: '#1d4ed8', fontSize: 26 }} />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: '#1d4ed8', fontWeight: 700, display: 'block' }}>
            Origem: anúncio {networkLabel(first)}
          </Typography>
          <Typography variant="body2" noWrap sx={{ fontWeight: 600, color: '#0f172a', fontSize: '0.8rem' }}>
            {title}
          </Typography>
        </Box>
        {touches.length > 1 && (
          <Chip size="small" label={`${touches.length} cliques`} sx={{ height: 20, fontSize: '0.65rem' }} />
        )}
        <IconButton size="small" sx={{ p: 0.25 }}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Divider sx={{ my: 1 }} />
        <Typography variant="caption" sx={{ color: '#475569', fontWeight: 700, display: 'block', mb: 0.5 }}>
          Primeiro contato · {formatDateTime(first.received_at)}
        </Typography>
        <TouchDetails touch={first} />
        {touches.length > 1 && (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography variant="caption" sx={{ color: '#475569', fontWeight: 700, display: 'block', mb: 0.5 }}>
              Último clique · {formatDateTime(latest.received_at)}
            </Typography>
            <TouchDetails touch={latest} />
          </>
        )}
      </Collapse>
    </Box>
  );
};

export default LeadAdOriginCard;
